import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { BarChart3, CircleDollarSign, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminPage, EmptyState, PillTabs, formatDateTime, formatNumber } from '../../../components/design-system/admin';
import { useUnitEconomicsApi } from '../../../common/hooks/use-unit-economics-api.hook';
import type { EconomicsRow, FeatureReviewResponse, UnitEconomicsResponse } from '../../../common/models/unit-economics.model';
import { AssumptionsPanel } from '../../../modules/superadmin/unit-economics/AssumptionsPanel';
import { CustomersTable } from '../../../modules/superadmin/unit-economics/CustomersTable';
import { CostSplitCard, EconomicsKpis } from '../../../modules/superadmin/unit-economics/EconomicsSummary';
import { FeatureReviewPanel } from '../../../modules/superadmin/unit-economics/FeatureReviewPanel';
import { VendorEconomicsSheet } from '../../../modules/superadmin/unit-economics/VendorEconomicsSheet';
import { monthLabel, recentMonths } from '../../../modules/superadmin/unit-economics/ue-format';

dayjs.extend(relativeTime);

type Tab = 'customers' | 'features' | 'assumptions';
const TABS: Tab[] = ['customers', 'features', 'assumptions'];

/**
 * Super-admin unit economics: per customer, what Ardealy earns, what it costs
 * to serve them, the margin, and the sales Ardealy influenced for them (the
 * renewal argument) — plus the feature review and every assumption behind the
 * numbers. Month, scope, tab and the open customer live in the URL.
 */
export const UnitEconomicsPage = () => {
  const { t, i18n } = useTranslation();
  const api = useUnitEconomicsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();
  const lang = (i18n.language || 'en').slice(0, 2);

  const months = useMemo(() => recentMonths(new Date(), 12), []);
  const month = months.includes(params.get('month') || '') ? (params.get('month') as string) : months[0];
  const scope = params.get('scope') === 'all' ? 'all' : 'real';
  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'customers';
  const vendorParam = params.get('vendor');

  const setParam = (key: string, value: string | null, fallback?: string) => {
    const p = new URLSearchParams(params);
    if (value === null || value === fallback) p.delete(key);
    else p.set(key, value);
    setParams(p, { replace: true });
  };

  const [data, setData] = useState<UnitEconomicsResponse | null>(null);
  const [review, setReview] = useState<FeatureReviewResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [reviewFailed, setReviewFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (fresh = false) => {
      setLoading(true);
      setFailed(false);
      try {
        setData(await apiRef.current.overview(month, scope, fresh));
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [month, scope],
  );

  const loadReview = useCallback(async (fresh = false) => {
    setReviewFailed(false);
    try {
      setReview(await apiRef.current.featureReview(fresh));
    } catch {
      setReviewFailed(true);
    }
  }, []);

  useEffect(() => {
    setData((d) => (d && d.month === month && d.scope === scope ? d : null));
    load();
  }, [load, month, scope]);

  useEffect(() => {
    if (tab === 'features' && !review && !reviewFailed) loadReview();
  }, [tab, review, reviewFailed, loadReview]);

  const refresh = async () => {
    await Promise.all([load(true), tab === 'features' ? loadReview(true) : Promise.resolve()]);
  };

  const openRow: EconomicsRow | null = (vendorParam && data?.rows.find((r) => r.vendorId === vendorParam)) || null;

  return (
    <AdminPage
      title={t('UNIT_ECONOMICS.TITLE')}
      header={
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('UNIT_ECONOMICS.TITLE')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('UNIT_ECONOMICS.DESCRIPTION')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data && (
              <span className="text-xs text-muted-foreground" title={formatDateTime(data.generatedAt)}>
                {t('UNIT_ECONOMICS.UPDATED', { time: dayjs(data.generatedAt).fromNow() })}
              </span>
            )}
            <Button variant="ghost" size="icon-sm" onClick={refresh} disabled={loading} aria-label={t('UNIT_ECONOMICS.REFRESH')} title={t('UNIT_ECONOMICS.REFRESH')}>
              <RefreshCw className={loading ? 'animate-spin' : undefined} />
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/costs">
                <CircleDollarSign />
                {t('UNIT_ECONOMICS.COSTS_LINK')}
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/vendor-usage">
                <BarChart3 />
                {t('UNIT_ECONOMICS.USAGE_LINK')}
              </Link>
            </Button>
            <Select value={scope} onValueChange={(v) => setParam('scope', v, 'real')}>
              <SelectTrigger className="h-9 w-[190px]" aria-label={t('UNIT_ECONOMICS.SCOPE_LABEL')} data-testid="ue-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="real">{t('UNIT_ECONOMICS.SCOPE.REAL')}</SelectItem>
                <SelectItem value="all">{t('UNIT_ECONOMICS.SCOPE.ALL')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={month} onValueChange={(v) => setParam('month', v, months[0])}>
              <SelectTrigger className="h-9 w-[170px]" aria-label={t('UNIT_ECONOMICS.MONTH_LABEL')} data-testid="ue-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m} value={m}>
                    {monthLabel(m, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      }
    >
      <PillTabs<Tab>
        value={tab}
        onChange={(v) => setParam('tab', v, 'customers')}
        ariaLabel={t('UNIT_ECONOMICS.TABS.LABEL')}
        className="self-start"
        options={[
          { value: 'customers', label: t('UNIT_ECONOMICS.TABS.CUSTOMERS') },
          { value: 'features', label: t('UNIT_ECONOMICS.TABS.FEATURES'), count: review?.counts.review },
          { value: 'assumptions', label: t('UNIT_ECONOMICS.TABS.ASSUMPTIONS') },
        ]}
      />

      {tab === 'customers' &&
        (failed && !data ? (
          <EmptyState
            title={t('UNIT_ECONOMICS.LOAD_FAILED')}
            action={
              <Button variant="outline" onClick={() => load(true)}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
            className="py-24"
          />
        ) : (
          <div className="flex flex-col gap-4" data-testid="ue-customers">
            {data?.current && (
              <p className="text-xs text-muted-foreground" data-testid="ue-mtd">
                {t('UNIT_ECONOMICS.MONTH_TO_DATE', { elapsed: formatNumber(data.elapsedDays, 0), days: data.daysInMonth })}
                {!data.totals.complete && ` ${t('UNIT_ECONOMICS.INCOMPLETE')}`}
              </p>
            )}
            {data && !data.current && !data.totals.complete && <p className="text-xs text-muted-foreground">{t('UNIT_ECONOMICS.INCOMPLETE')}</p>}
            <EconomicsKpis data={data} />
            <CostSplitCard data={data} />
            <CustomersTable rows={data?.rows ?? null} assumptions={data?.assumptions ?? null} loading={loading} onOpen={(r) => setParam('vendor', r.vendorId)} />
          </div>
        ))}

      {tab === 'features' &&
        (reviewFailed && !review ? (
          <EmptyState
            title={t('UNIT_ECONOMICS.REVIEW.LOAD_FAILED')}
            action={
              <Button variant="outline" onClick={() => loadReview(true)}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
            className="py-24"
          />
        ) : (
          <FeatureReviewPanel data={review} />
        ))}

      {tab === 'assumptions' && <AssumptionsPanel assumptions={data?.assumptions ?? review?.assumptions ?? null} />}

      <VendorEconomicsSheet row={openRow} month={month} onClose={() => setParam('vendor', null)} />
    </AdminPage>
  );
};

export default UnitEconomicsPage;
